import { validate as uuidValidate } from 'uuid';

/**
 * Walidacja parametrów zapytania strony produktu – zabezpieczenie przed XSS i nieprawidłowymi danymi.
 * Wartości są używane po stronie serwera i klienta (configurator) – nigdy nie przekazuj surowego query do DOM/URL bez sanityzacji.
 */

/** Bezpieczny zestaw znaków dla literal object ID (dowolny prefiks, np. CRE-..., OBJ-..., UUID) – blokuje XSS. Myślnik na początku klasy, żeby był literalny (nie zakres). */
const SAFE_OBJECT_ID_REGEX = /^[-A-Za-z0-9_]{1,64}$/;
export function sanitizeObjectUuid(value: string | null): string | null {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length > 64) return null;
  if (uuidValidate(trimmed.slice(0, 36))) return trimmed.slice(0, 36);
  if (SAFE_OBJECT_ID_REGEX.test(trimmed)) return trimmed;
  return null;
}
export function sanitizePresetIndex(value: string | null): string | null {
  if (!value || typeof value !== 'string') return null;
  return /^\d{1,10}$/.test(value) ? value : null;
}
export function sanitizeCart(value: string | null): string | null {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim().slice(0, 128);
  if (uuidValidate(trimmed)) return trimmed;
  return /^[A-Za-z0-9_.-]+$/.test(trimmed) ? trimmed : null;
}
export function sanitizeTrackingValue(value: string): string {
  return value.replace(/[^A-Za-z0-9_.~-]/g, '').slice(0, 256);
}
export function sanitizeProductSlug(slugString: string): string {
  const decodedURI = decodeURI(slugString);
  let slug: string = decodedURI.includes('?') ? decodedURI.slice(0, decodedURI.indexOf('?')) : decodedURI;
  if (slug.includes('&')) slug = slug.slice(0, slug.indexOf('&'));
  return slug;
}
const TRACKING_PARAM_NAMES = ['gclid', 'glcid', 'gclsrc', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'msclkid', 'ref'];

/**
 * Zwraca ścieżkę (pathname + query) z tylko sanityzowanymi parametrami.
 * Użyj do og:url, canonical itd., żeby nie odbijać w HTML surowego query z payloadem XSS.
 */
export function getSafeProductPagePath(asPath: string): string {
  const qIndex = asPath.indexOf('?');
  const pathname = qIndex >= 0 ? asPath.slice(0, qIndex) : asPath;
  const search = qIndex >= 0 ? asPath.slice(qIndex + 1) : '';
  if (!search) return pathname;
  const params = new URLSearchParams(search);
  const safe = new URLSearchParams();
  TRACKING_PARAM_NAMES.forEach(name => {
    const v = params.get(name);
    if (v) {
      const s = sanitizeTrackingValue(v);
      if (s) safe.set(name, s);
    }
  });
  const objectUuid = sanitizeObjectUuid(params.get('objectUuid'));
  const presetIndex = sanitizePresetIndex(params.get('presetIndex'));
  const cart = sanitizeCart(params.get('cart'));
  if (objectUuid) safe.set('objectUuid', objectUuid);
  if (presetIndex) safe.set('presetIndex', presetIndex);
  if (cart) safe.set('cart', cart);
  const q = safe.toString();
  return q ? `${pathname}?${q}` : pathname;
}